<template>
  <div class="step-container client">
    <help-drawer :title="helpTitle">
      <div>
        <p>
          Collecting this information helps your financial professional to
          fulfill regulatory obligations.
        </p>
      </div>
    </help-drawer>
    <v-form name="AnnualIncome" class="form" data-test-id="annual-income-form">
      <v-container class="client-step-container">
        <syncing-indicator :pageName="MENUS.CLIENT.ANNUAL_INCOME.id"></syncing-indicator>
        <v-row class="mb-8">
          <v-col class="instructions">
            <h1 data-test-id="annual-income-header">
              <span class="headerlight">
                What's your <strong>annual income?</strong>
              </span>
            </h1>
            <div class="help-link">
              <a @click="toggleHelp()" data-test-id="help-link">{{ helpTitle }}</a>
            </div>
          </v-col>
        </v-row>
        
        <!-- Show syncing message when syncing -->
        <div v-if="isSyncing" class="syncing-message" data-test-id="syncing-message">
          <p>Saving your annual income...</p>
        </div>
        
        <!-- Show data submitted message only when completed, not syncing, and form hidden -->
        <data-submitted-message
          v-if="isAnnualIncomeComplete && formHidden && !isSyncing"
          :submitted="isAnnualIncomeComplete"
          v-on:hide-form="toggleFormState"
          data-test-id="data-submitted-message"
        />
        
        <!-- Hide form content when syncing or when formHidden is true -->
        <v-column class="justify-center full col-12 col-sm-4" v-if="!formHidden && !isSyncing">
            <div class="faux-label required" data-test-id="annual-income-label">Enter annual income in USD</div>
              <v-text-field
                outlined
                type="number"
                maxlength="256"
                name="annualIncome"
                aria-required="true"
                v-model.trim="annualIncome"
                :backgroundColor="inputBackgroundColor"
                @blur="$v.annualIncome.$touch()"
                :error-messages="annualIncomeErrors"
                hide-details="auto"
                data-test-id="annual-income-input"
              />
            <div class="skip-for-now">
              <a @click="skip()" data-test-id="skip-button">Skip for now</a>
            </div>
        </v-column>
      </v-container>
    </v-form>
    <bottom-nav v-on:go-prev="prev()" v-on:go-next="next()" data-test-id="annual-income-nav"></bottom-nav>
  </div>
</template>

<script>
import HelpDrawer from '@/components/HelpDrawerComponent';
import BottomNav from '@/components/common/BottomNav';
import DataSubmittedMessage from '@/components/client/DataSubmittedMessage';
import InterviewClientMixin from '@/common/mixins/interview.client.mixin';
import { required } from 'vuelidate/lib/validators';
import { MENUS } from '@/common/constants';
import SyncingIndicator from '@/components/common/SyncingIndicator.vue';

export default {
  name: 'AnnualIncome',

  components: { BottomNav, HelpDrawer, DataSubmittedMessage, SyncingIndicator },
  mixins: [InterviewClientMixin],

  data: () => ({
    annualIncome: '',
    MENUS: MENUS,
  }),

  validations() {
    return {
      annualIncome: {
        required,
      },
      validationGroup: ['annualIncome'],
    };
  },

  computed: {
    // Is annual income complete
    isAnnualIncomeComplete() {
      return this.isPageComplete(MENUS.CLIENT.ANNUAL_INCOME.id);
    },
    
    // Check if the page is currently syncing
    isSyncing() {
      const page = this.getPage(MENUS.CLIENT.ANNUAL_INCOME.id);
      return page && page.status === 'IN_PROGRESS';
    },
    /* Dynamic errors */
    annualIncomeErrors() {
      const errors = [];
      if (!this.$v.annualIncome.$dirty) return errors;
      !this.$v.annualIncome.required &&
        errors.push('Please enter amount in USD');
      return errors;
    },
    helpTitle() {
      return 'Why do you need this?';
    },
  },

  methods: {
    /* Nav to Next Page in flow */
    next(route = null) {      
      let to = route || this.pages[this.pageIndex + 1];
  
      if (!this.formHidden) {
        this.$v.$touch();
        if (this.$v.$invalid) return;
      } else {
        this.goNext(to);
        return;
      }

      const data = {
        annualIncome: this.annualIncome,
      };

      // Mark page as syncing immediately for better user feedback
      this.markPageAsSyncing(this.pages[this.pageIndex]);

      this.completeStep(this.pages[this.pageIndex], data)
        .then(() => {
          // Set formHidden to true after successful completion
          this.formHidden = true;
          this.goNext(to);
        })
        .catch(() => {});
    },

    /* Nav to Previous Page in flow */
    prev() {
      this.goPrev(this.pages[this.pageIndex - 1]);
    },

    /* Skip to next Page in flow */
    skip() {
      // Fetch updated interview progress before navigation
      this.$store.dispatch('GET_INTERVIEW_PROGRESS').finally(() => {        
        this.goNext(this.pages[this.pageIndex + 1]);
      });
    },
    
    // Toggle form state with validation reset
    toggleFormState(hide) {
      this.formHidden = hide;
      
      // Reset form validation
      this.$nextTick(() => {
        if (this.$v) {
          this.$v.$reset();
        }
      });
    },
    
    // Update formHidden state when sync status changes
    handleSyncStatusChange() {
      // If completed, hide the form
      if (this.isAnnualIncomeComplete) {
        this.formHidden = true;
      }
    }
  },
  
  watch: {
    // Watch for changes in the sync status
    isSyncing(newVal, oldVal) {
      if (oldVal === true && newVal === false) {
        // Sync has completed, check if form should be hidden
        this.$nextTick(() => {
          this.handleSyncStatusChange();
        });
      }
    },
    // Watch for changes in the completion status
    isAnnualIncomeComplete(newVal) {
      if (newVal === true) {
        this.formHidden = true;
      }
    }
  },
  
  created() {
    this.interviewPages.addEmploymentPages();
    this.pages = this.interviewPages.getInterviewPages();
    this.pageIndex = this.pages.indexOf(MENUS.CLIENT.ANNUAL_INCOME.id);
    // Initialize form data for current interview
    this.formHidden = this.isAnnualIncomeComplete;
  },
  
  mounted() {
    // Check form state on mount
    this.$nextTick(() => {
      // If annual income is complete, hide the form
      if (this.isAnnualIncomeComplete) {
        this.formHidden = true;
      }
    });
    
    // Start polling for updates to keep synced state updated
    this.$store.dispatch('GET_INTERVIEW_PROGRESS');
  }
};
</script>
<style lang="scss" scoped>
.syncing-message {
  text-align: center;
  margin: 20px 0;
  
  p {
    color: #666;
    font-style: italic;
  }
}
</style>
